import React, { useState } from "react";
import { View, RefreshControl } from "react-native";
import { Text, ActivityIndicator } from "react-native-paper";
import { useQuery, useMutation } from "@apollo/client";
import { Feather } from "@expo/vector-icons";
import moment from "moment";

import { colors } from "../../../theme";
import { Button, ScreenContainer, ScrollableContainer } from "../../components";
import { ScreenProps } from "../types";
import { MY_FACILITY_QUERY, FACILITY_HOLE_IN_ONES_QUERY, CREATE_HOLE_IN_ONE_MUTATION } from "./queries";
import { MyFacilityQuery, FacilityHoleInOnesQuery, CreateHoleInOneMutation } from "./types";
import { HoleInOne, CreateHoleInOneInput } from "./types";
import useSnackBar from "../../hooks/useSnackBar";
import AddHoleInOneModal from "./AddHoleInOneModal";

export const navigationTitle = "My Facility";

const HoleInOneCard = ({ holeInOne }: { holeInOne: HoleInOne }) => {
  const formattedDate = moment(holeInOne.occurredAt).format("MMM D, YYYY");

  return (
    <View className="bg-white rounded-lg shadow-sm p-6 mb-4">
      <View className="flex-row justify-between items-start mb-2">
        <View className="flex-1">
          <Text className="text-regular-tight font-700 text-ink-darkest">{holeInOne.golferName}</Text>
          <Text className="text-small-normal text-ink-darker font-700">{holeInOne.courseName}</Text>
        </View>
        <View className="items-end">
          <Text className="text-small-normal text-ink-darker">{formattedDate}</Text>
        </View>
      </View>

      <View className="flex-row justify-between items-center mt-4">
        <View className="flex-row items-center">
          <View className="bg-primary rounded-full w-8 h-8 items-center justify-center mr-3">
            <Text className="text-white font-700 text-small-normal">{holeInOne.holeNumber}</Text>
          </View>
          <Text className="text-small-normal text-ink-darker">Hole {holeInOne.holeNumber}</Text>
        </View>

        {holeInOne.yardage && <Text className="text-small-normal text-ink-darker">{holeInOne.yardage} yards</Text>}
      </View>

      {holeInOne.clubUsed && <Text className="text-small-normal text-ink-darker mt-2">Club: {holeInOne.clubUsed}</Text>}

      {holeInOne.clubBrand && holeInOne.clubModel && (
        <Text className="text-small-normal text-ink-darker mt-1">
          {holeInOne.clubBrand} {holeInOne.clubModel}
        </Text>
      )}

      {holeInOne.ballUsed && <Text className="text-small-normal text-ink-darker mt-1">Ball: {holeInOne.ballUsed}</Text>}
    </View>
  );
};

const EmptyState = ({ onAddPress }: { onAddPress: () => void }) => (
  <View className="flex-1 items-center justify-center py-12">
    <View className="bg-sky-lighter rounded-full w-16 h-16 items-center justify-center mb-4">
      <Feather name="target" size={32} color={colors.primary.DEFAULT} />
    </View>
    <Text className="text-regular-tight font-700 text-ink-darkest mb-2">No Hole-in-Ones Yet</Text>
    <Text className="text-small-normal text-ink-darker text-center mb-6 px-8">
      Start recording hole-in-ones for your members and golfers at your facility.
    </Text>
    <Button mode="contained" onPress={onAddPress}>
      Add Your First Hole-in-One
    </Button>
  </View>
);

export default function MyFacilityScreen({ navigation }: ScreenProps<"MyFacility">) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const showSnackBar = useSnackBar();

  // Get current user's facility information
  const {
    data: facilityData,
    loading: facilityLoading,
    error: facilityError,
  } = useQuery<MyFacilityQuery>(MY_FACILITY_QUERY, {
    errorPolicy: "all",
    onError: (error) => {
      console.error("Facility query error:", error);
    },
  });

  // Get all unique facilities from employments
  const employments = facilityData?.currentPerson?.employments || [];
  const userFacilities = employments
    .map((emp) => emp.facility)
    .filter((facility, index, array) => array.findIndex((f) => f.id === facility.id) === index); // Remove duplicates by id

  // For backwards compatibility, get primary employment
  const primaryEmployment = employments.find((emp) => emp.isPrimary);

  // Query hole-in-ones for all user facilities
  const facilityIds = userFacilities.map((f) => f.id);

  // Single query for all facilities
  const {
    data: holeInOnesData,
    loading: holeInOnesLoading,
    refetch,
    error: holeInOnesError,
  } = useQuery<FacilityHoleInOnesQuery>(FACILITY_HOLE_IN_ONES_QUERY, {
    variables: {
      facilityIds,
      limit: 50,
      offset: 0,
    },
    skip: facilityIds.length === 0,
    errorPolicy: "all",
    onError: (error) => {
      console.error("Multiple facilities hole-in-ones query error:", error);
    },
  });

  // Create hole-in-one mutation
  const [createHoleInOne, { loading: mutationLoading }] = useMutation<CreateHoleInOneMutation>(CREATE_HOLE_IN_ONE_MUTATION, {
    onCompleted: (data) => {
      console.log("Mutation completed:", data);
      if (data.createHoleInOne.holeInOne) {
        showSnackBar({
          message: "Hole-in-one recorded successfully!",
          duration: 3000,
          icon: "check-circle",
        });
        setIsModalVisible(false);
        refetch();
      } else if (data.createHoleInOne.errors.length > 0) {
        const errorMsg = data.createHoleInOne.errors[0] || "Failed to record hole-in-one";
        showSnackBar({
          message: errorMsg,
          duration: 3000,
          icon: "x-circle",
        });
      }
    },
    onError: (error) => {
      console.error("Mutation error:", error);
      showSnackBar({
        message: "Failed to record hole-in-one",
        duration: 3000,
        icon: "x-circle",
      });
    },
  });

  const handleCreateHoleInOne = async (input: CreateHoleInOneInput) => {
    try {
      console.log("Starting mutation with input:", input);

      const result = await createHoleInOne({
        variables: {
          input: input,
        },
      });

      console.log("Mutation result:", result);
    } catch (error) {
      console.error("Error in handleCreateHoleInOne:", error);
      throw error;
    }
  };

  if (facilityLoading) {
    return (
      <ScreenContainer testID="MyFacilityScreen" navigationTitle="My Facility">
        <View className="flex-1 justify-center items-center">
          <ActivityIndicator size="large" />
        </View>
      </ScreenContainer>
    );
  }

  if (facilityError || !primaryEmployment?.facility) {
    return (
      <ScreenContainer testID="MyFacilityScreen" navigationTitle="My Facility">
        <View className="flex-1 justify-center items-center p-6">
          <Text className="text-center text-ink-darker">
            {facilityError ? "Error loading facility information" : "No facility found for your account"}
          </Text>
          <Button mode="outlined" onPress={() => navigation.goBack()} className="mt-4">
            Go Back
          </Button>
        </View>
      </ScreenContainer>
    );
  }

  const facility = primaryEmployment.facility;
  const holeInOnes = holeInOnesData?.facilityHoleInOnes?.entries || [];

  return (
    <ScreenContainer testID="MyFacilityScreen" navigationTitle="My Facility">
      <ScrollableContainer refreshControl={<RefreshControl refreshing={holeInOnesLoading} onRefresh={() => refetch()} />}>
        <View className="p-6">
          {/* Facility Header */}
          <View className="mb-6">
            <Text className="text-2xl font-700 text-ink-darkest mb-2">{facility.name}</Text>
            <Text className="text-small-normal text-ink-dark">Manage hole-in-one records for your facility</Text>
          </View>

          {/* Add New Button */}
          <View className="mb-6">
            <Button mode="contained" onPress={() => setIsModalVisible(true)} className="w-full" testID="add-hole-in-one-button">
              Record New Hole-in-One
            </Button>
          </View>

          {/* Hole-in-Ones List */}
          <View className="mb-6">
            <Text className="text-regular-tight font-700 text-ink-darkest mb-4">
              Recorded Hole-in-Ones ({holeInOnes.length})
            </Text>

            {holeInOnesLoading ? (
              <View className="py-8 items-center">
                <ActivityIndicator />
                <Text className="text-small-normal text-ink-darker mt-2">Loading hole-in-ones...</Text>
              </View>
            ) : holeInOnesError ? (
              <View className="py-8 items-center">
                <Text className="text-small-normal text-ink-darker text-center">
                  Error loading hole-in-ones. Please try again.
                </Text>
                <Button mode="outlined" onPress={() => refetch()} className="mt-2">
                  Retry
                </Button>
              </View>
            ) : holeInOnes.length === 0 ? (
              <EmptyState onAddPress={() => setIsModalVisible(true)} />
            ) : (
              <View>
                {holeInOnes.map((holeInOne) => (
                  <HoleInOneCard key={holeInOne.id} holeInOne={holeInOne} />
                ))}
              </View>
            )}
          </View>
        </View>
      </ScrollableContainer>

      <AddHoleInOneModal
        visible={isModalVisible}
        onDismiss={() => setIsModalVisible(false)}
        onSubmit={handleCreateHoleInOne}
        loading={mutationLoading}
        userFacilities={userFacilities}
      />
    </ScreenContainer>
  );
}
